import type { LoSiteBlock } from '@/lib/lyrraone-site-blocks';

export interface LoSiteElementStyle {
  color?: string;
  backgroundColor?: string;
  hoverBackgroundColor?: string;
  hoverColor?: string;
  fontSize?: string;
  fontWeight?: string;
  fontFamily?: string;
  textAlign?: 'left' | 'center' | 'right';
  lineHeight?: string;
}

export const LO_SITE_FONT_SIZES = ['0.75rem', '0.875rem', '1rem', '1.125rem', '1.25rem', '1.5rem', '1.875rem', '2.25rem', '3rem', '3.75rem'] as const;

export const LO_SITE_FONT_FAMILIES = [
  { label: 'Par défaut', value: '' },
  { label: 'Titres LyrraOne', value: 'var(--lo-font-title)' },
  { label: 'Corps LyrraOne', value: 'var(--lo-font-body)' },
  { label: 'Sans-serif', value: 'ui-sans-serif, system-ui, sans-serif' },
  { label: 'Serif', value: 'Georgia, ui-serif, serif' },
  { label: 'Monospace', value: 'ui-monospace, monospace' },
] as const;

export const LO_SITE_FONT_WEIGHTS = [
  { label: 'Normal', value: '400' },
  { label: 'Medium', value: '500' },
  { label: 'Semi-gras', value: '600' },
  { label: 'Gras', value: '700' },
  { label: 'Extra-gras', value: '800' },
] as const;

/** Couleurs du bouton principal LyrraOne (classe `.lo-btn-primary`). */
export const LO_SITE_PRIMARY_BUTTON_DEFAULTS: LoSiteElementStyle = {
  color: '#ffffff',
  backgroundColor: '#2563eb',
  hoverBackgroundColor: '#1d4ed8',
  hoverColor: '#ffffff',
};

export function getBlockElementStyle(block: LoSiteBlock, elementKey: string): LoSiteElementStyle | undefined {
  return block.styles?.[elementKey];
}

export function patchBlockElementStyle(
  block: LoSiteBlock,
  elementKey: string,
  patch: Partial<LoSiteElementStyle>,
): Partial<LoSiteBlock> {
  const current = block.styles?.[elementKey] ?? {};
  const merged = { ...current, ...patch };
  const cleaned = Object.fromEntries(
    Object.entries(merged).filter(([, v]) => v !== undefined && v !== ''),
  ) as LoSiteElementStyle;
  const nextStyles = { ...(block.styles ?? {}) };
  if (Object.keys(cleaned).length === 0) {
    delete nextStyles[elementKey];
  } else {
    nextStyles[elementKey] = cleaned;
  }
  return {
    styles: Object.keys(nextStyles).length > 0 ? nextStyles : undefined,
  };
}

export function elementStyleToCss(style?: LoSiteElementStyle): React.CSSProperties {
  if (!style) return {};
  const css: React.CSSProperties & Record<string, string | undefined> = {};
  if (style.color) {
    css.color = style.color;
    css['--lo-el-color'] = style.color;
  }
  if (style.backgroundColor) {
    css.backgroundColor = style.backgroundColor;
    css['--lo-el-bg'] = style.backgroundColor;
  }
  if (style.fontSize) {
    css.fontSize = style.fontSize;
    css['--lo-el-font-size'] = style.fontSize;
  }
  if (style.fontWeight) {
    css.fontWeight = style.fontWeight;
    css['--lo-el-font-weight'] = style.fontWeight;
  }
  if (style.fontFamily) {
    css.fontFamily = style.fontFamily;
    css['--lo-el-font-family'] = style.fontFamily;
  }
  if (style.textAlign) {
    css.textAlign = style.textAlign;
    css['--lo-el-text-align'] = style.textAlign;
  }
  if (style.lineHeight) {
    css.lineHeight = style.lineHeight;
    css['--lo-el-line-height'] = style.lineHeight;
  }
  return css;
}

export function hasElementCustomStyle(style?: LoSiteElementStyle): boolean {
  if (!style) return false;
  return Object.values(style).some((v) => v !== undefined && v !== '');
}

export function elementStyleClass(style?: LoSiteElementStyle): string {
  return hasElementCustomStyle(style) ? 'lo-site-el-custom' : '';
}

export function buttonStyleToCss(style?: LoSiteElementStyle): React.CSSProperties & Record<string, string | undefined> {
  const css = elementStyleToCss(style) as React.CSSProperties & Record<string, string | undefined>;
  if (style?.backgroundColor) {
    css['--lo-btn-bg'] = style.backgroundColor;
    css.backgroundColor = style.backgroundColor;
  }
  if (style?.color) {
    css.color = style.color;
    css['--lo-btn-color'] = style.color;
  }
  if (style?.hoverBackgroundColor) css['--lo-btn-hover-bg'] = style.hoverBackgroundColor;
  if (style?.hoverColor) css['--lo-btn-hover-color'] = style.hoverColor;
  return css;
}

export function hasCustomButtonStyle(style?: LoSiteElementStyle): boolean {
  if (!style) return false;
  return Boolean(style.backgroundColor || style.color || style.hoverBackgroundColor || style.hoverColor);
}

/** Style effectif affiché dans l’éditeur (thème + surcharges enregistrées). */
export function getButtonElementDisplayStyle(
  block: LoSiteBlock,
  elementKey: string,
): { stored: LoSiteElementStyle; display: LoSiteElementStyle; usesThemeDefaults: boolean } {
  const stored = getBlockElementStyle(block, elementKey) ?? {};
  const usesThemeDefaults = !hasCustomButtonStyle(stored);
  return {
    stored,
    display: { ...LO_SITE_PRIMARY_BUTTON_DEFAULTS, ...stored },
    usesThemeDefaults,
  };
}

export function getElementLabel(elementKey: string): string {
  const map: Record<string, string> = {
    'hero.title': 'Titre principal',
    'hero.subtitle': 'Sous-titre',
    'hero.cta': 'Bouton',
    'cta.button': 'Bouton',
    'text.body': 'Contenu',
    'heading.title': 'Titre',
    'subheading.title': 'Sous-titre',
    'paragraph.body': 'Paragraphe',
    'features.title': 'Titre de section',
    'features.subtitle': 'Sous-titre',
    'features.kicker': 'Surtitre',
    'features.footer': 'Texte de bas de section',
    'features.cta': 'Bouton',
    'testimonials.title': 'Titre témoignages',
    'faq.title': 'Titre FAQ',
    'formations.title': 'Titre de section',
    'image.caption': 'Légende',
  };
  if (map[elementKey]) return map[elementKey];
  const itemMatch = elementKey.match(/^features\.item\.(\d+)\.(title|description)$/);
  if (itemMatch) {
    const idx = Number(itemMatch[1]) + 1;
    return itemMatch[2] === 'title' ? `Carte ${idx} — titre` : `Carte ${idx} — description`;
  }
  const colMatch = elementKey.match(/^columns[23]\.col\.(\d+)$/);
  if (colMatch) return `Colonne ${Number(colMatch[1]) + 1}`;
  return elementKey;
}

export function isButtonElement(elementKey: string): boolean {
  return elementKey === 'hero.cta' || elementKey === 'cta.button';
}

export function isRichTextElement(elementKey: string): boolean {
  return elementKey === 'text.body';
}

export function listBlockElementKeys(block: LoSiteBlock): string[] {
  switch (block.type) {
    case 'hero':
      return ['hero.title', 'hero.subtitle', 'hero.cta'].filter((key) => {
        if (key === 'hero.title') return Boolean(block.title);
        if (key === 'hero.subtitle') return Boolean(block.subtitle);
        if (key === 'hero.cta') return Boolean(block.ctaText);
        return false;
      });
    case 'cta':
      return block.url ? ['cta.button'] : [];
    case 'text':
      return ['text.body'];
    case 'heading':
      return ['heading.title'];
    case 'subheading':
      return ['subheading.title'];
    case 'paragraph':
      return ['paragraph.body'];
    case 'columns2':
      return (block.columns ?? []).map((_, i) => `columns2.col.${i}`);
    case 'columns3':
      return (block.columns ?? []).map((_, i) => `columns3.col.${i}`);
    case 'features':
      return [
        ...(block.featuresKicker ? ['features.kicker'] : []),
        ...(block.title ? ['features.title'] : []),
        ...(block.subtitle ? ['features.subtitle'] : []),
        ...(block.items ?? []).flatMap((_, i) => [
          `features.item.${i}.title`,
          `features.item.${i}.description`,
        ]),
        ...(block.featuresFooter ? ['features.footer'] : []),
        ...(block.featuresVariant === 'cta-band' && block.ctaText ? ['features.cta'] : []),
      ];
    case 'testimonials':
      return [
        ...(block.title ? ['testimonials.title'] : []),
        ...(block.subtitle ? ['testimonials.subtitle'] : []),
        ...(block.testimonials ?? []).flatMap((_, i) => [
          `testimonials.item.${i}.quote`,
          `testimonials.item.${i}.name`,
          `testimonials.item.${i}.role`,
        ]),
      ];
    case 'faq':
      return [
        ...(block.title ? ['faq.title'] : []),
        ...(block.faqItems ?? []).flatMap((_, i) => [
          `faq.item.${i}.question`,
          `faq.item.${i}.answer`,
        ]),
      ];
    case 'formations':
      return block.title ? ['formations.title'] : ['formations.title'];
    case 'image':
      return block.caption ? ['image.caption'] : [];
    default:
      return [];
  }
}

export function getElementTextContent(block: LoSiteBlock, elementKey: string): string {
  if (elementKey === 'hero.title') return block.title ?? '';
  if (elementKey === 'hero.subtitle') return block.subtitle ?? '';
  if (elementKey === 'hero.cta') return block.ctaText ?? '';
  if (elementKey === 'cta.button') return block.text ?? '';
  if (elementKey === 'text.body') return block.body ?? '';
  if (elementKey === 'heading.title') return block.title ?? '';
  if (elementKey === 'subheading.title') return block.title ?? '';
  if (elementKey === 'paragraph.body') return block.body ?? '';
  if (elementKey === 'features.title') return block.title ?? '';
  if (elementKey === 'features.subtitle') return block.subtitle ?? '';
  if (elementKey === 'features.kicker') return block.featuresKicker ?? '';
  if (elementKey === 'features.footer') return block.featuresFooter ?? '';
  if (elementKey === 'features.cta') return block.ctaText ?? '';
  if (elementKey === 'testimonials.title') return block.title ?? '';
  if (elementKey === 'testimonials.subtitle') return block.subtitle ?? '';
  if (elementKey === 'faq.title') return block.title ?? '';
  if (elementKey === 'formations.title') return block.title ?? '';
  if (elementKey === 'image.caption') return block.caption ?? '';
  const itemMatch = elementKey.match(/^features\.item\.(\d+)\.(title|description)$/);
  if (itemMatch) {
    const idx = Number(itemMatch[1]);
    const item = block.items?.[idx];
    if (!item) return '';
    return itemMatch[2] === 'title' ? item.title : item.description;
  }
  const colMatch = elementKey.match(/^columns[23]\.col\.(\d+)$/);
  if (colMatch) {
    const idx = Number(colMatch[1]);
    return block.columns?.[idx]?.body ?? '';
  }
  const testimonialMatch = elementKey.match(/^testimonials\.item\.(\d+)\.(quote|name|role)$/);
  if (testimonialMatch) {
    const idx = Number(testimonialMatch[1]);
    const item = block.testimonials?.[idx];
    if (!item) return '';
    const field = testimonialMatch[2] as 'quote' | 'name' | 'role';
    return item[field] ?? '';
  }
  const faqMatch = elementKey.match(/^faq\.item\.(\d+)\.(question|answer)$/);
  if (faqMatch) {
    const idx = Number(faqMatch[1]);
    const item = block.faqItems?.[idx];
    if (!item) return '';
    return faqMatch[2] === 'question' ? item.question : item.answer;
  }
  return '';
}

export function patchElementTextContent(
  block: LoSiteBlock,
  elementKey: string,
  value: string,
): Partial<LoSiteBlock> {
  if (elementKey === 'hero.title') return { title: value };
  if (elementKey === 'hero.subtitle') return { subtitle: value };
  if (elementKey === 'hero.cta') return { ctaText: value };
  if (elementKey === 'cta.button') return { text: value };
  if (elementKey === 'text.body') return { body: value };
  if (elementKey === 'heading.title') return { title: value };
  if (elementKey === 'subheading.title') return { title: value };
  if (elementKey === 'paragraph.body') return { body: value };
  if (elementKey === 'features.title') return { title: value };
  if (elementKey === 'features.subtitle') return { subtitle: value };
  if (elementKey === 'features.kicker') return { featuresKicker: value };
  if (elementKey === 'features.footer') return { featuresFooter: value };
  if (elementKey === 'features.cta') return { ctaText: value };
  if (elementKey === 'testimonials.title') return { title: value };
  if (elementKey === 'testimonials.subtitle') return { subtitle: value };
  if (elementKey === 'faq.title') return { title: value };
  const testimonialMatch = elementKey.match(/^testimonials\.item\.(\d+)\.(quote|name|role)$/);
  if (testimonialMatch) {
    const idx = Number(testimonialMatch[1]);
    const field = testimonialMatch[2] as 'quote' | 'name' | 'role';
    const testimonials = [...(block.testimonials ?? [])];
    if (!testimonials[idx]) return {};
    testimonials[idx] = { ...testimonials[idx], [field]: value };
    return { testimonials };
  }
  const faqMatch = elementKey.match(/^faq\.item\.(\d+)\.(question|answer)$/);
  if (faqMatch) {
    const idx = Number(faqMatch[1]);
    const field = faqMatch[2] as 'question' | 'answer';
    const faqItems = [...(block.faqItems ?? [])];
    if (!faqItems[idx]) return {};
    faqItems[idx] = { ...faqItems[idx], [field]: value };
    return { faqItems };
  }
  if (elementKey === 'formations.title') return { title: value };
  if (elementKey === 'image.caption') return { caption: value };
  const itemMatch = elementKey.match(/^features\.item\.(\d+)\.(title|description)$/);
  if (itemMatch) {
    const idx = Number(itemMatch[1]);
    const field = itemMatch[2] as 'title' | 'description';
    const items = [...(block.items ?? [])];
    if (!items[idx]) return {};
    items[idx] = { ...items[idx], [field]: value };
    return { items };
  }
  const colMatch = elementKey.match(/^columns[23]\.col\.(\d+)$/);
  if (colMatch) {
    const idx = Number(colMatch[1]);
    const columns = [...(block.columns ?? [])];
    if (!columns[idx]) return {};
    columns[idx] = { ...columns[idx], body: value };
    return { columns };
  }
  return {};
}
